import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosError } from 'axios';
import { ApiService } from 'services/ApiService';
import { axiosErrorMessage, getProblemDetail } from 'utils/apiError';

/** Which endpoint family the mutations target. */
export type AppointmentActionsScope = 'customer' | 'team';

interface UseAppointmentActionsOptions {
  /** 'customer' → /v1/me/appointments; 'team' → /v1/teams/{teamId}/appointments (rule-bypass). */
  scope: AppointmentActionsScope;
  /** Required when scope is 'team' — carried as a route param only, never in a body. */
  teamId?: string;
  /** Awaited after every successful mutation (plan Decision 5 — the refetched
   *  outputJson stays the display source of truth; no optimistic time edits). */
  onRefresh?: () => Promise<void> | void;
}

/** Public shape returned by {@link useAppointmentActions}. */
interface UseAppointmentActionsResult {
  cancelAppointment: (submissionId: string) => Promise<boolean>;
  rescheduleAppointment: (
    submissionId: string,
    newAppointmentTimeUtc: string,
  ) => Promise<boolean>;
  isCancelling: boolean;
  isRescheduling: boolean;
  error: string | null;
}

export const APPT_ACTIONS_CANCEL_FALLBACK = 'Failed to cancel the appointment.';
export const APPT_ACTIONS_RESCHEDULE_FALLBACK = 'Failed to reschedule the appointment.';
export const APPT_ACTIONS_TEAM_ID_REQUIRED =
  'A team is required to perform this action.';

/**
 * Maps a mutation failure to a display string:
 *  - service-layer plain Errors are already normalized by ApiService
 *    (422 reason verbatim / fixed 404 copy) → pass through
 *  - raw axios 422 → the backend's ProblemDetails reason verbatim via
 *    getProblemDetail (rule-block reason or validation message)
 *  - any other axios/unknown error → axiosErrorMessage status mapping
 *    (404/403 fixed copy, caller fallback otherwise)
 */
function actionErrorMessage(err: unknown, fallback: string): string {
  const status = (err as AxiosError | null | undefined)?.response?.status;
  if (typeof status === 'number') {
    if (status === 422) return getProblemDetail(err) ?? fallback;
    return axiosErrorMessage(err, fallback);
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * Customer/team appointment cancel + reschedule mutations. Layering hook
 * between the actions panel and ApiService — the server stays the source of
 * truth: on success the host's `onRefresh` re-fetches, on failure the status-
 * mapped reason (422 verbatim) is exposed via `error`. Mutations return a
 * boolean so callers close dialogs/forms only on success (M4 contract).
 */
export default function useAppointmentActions({
  scope,
  teamId,
  onRefresh,
}: UseAppointmentActionsOptions): UseAppointmentActionsResult {
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [isRescheduling, setIsRescheduling] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const runAction = useCallback(
    async (
      action: () => Promise<void>,
      setBusy: (busy: boolean) => void,
      fallback: string,
    ): Promise<boolean> => {
      setBusy(true);
      setError(null);
      try {
        await action();
      } catch (e) {
        if (mountedRef.current) {
          setError(actionErrorMessage(e, fallback));
          setBusy(false);
        }
        return false;
      }
      // Refresh is best-effort: the host's refresh path surfaces its own errors.
      try {
        await onRefresh?.();
      } catch {
        // host handles refresh errors
      }
      if (mountedRef.current) setBusy(false);
      return true;
    },
    [onRefresh],
  );

  const cancelAppointment = useCallback(
    async (submissionId: string): Promise<boolean> => {
      if (scope === 'team') {
        if (!teamId) {
          setError(APPT_ACTIONS_TEAM_ID_REQUIRED);
          return false;
        }
        const id = teamId;
        return runAction(
          () => ApiService.teamCancelAppointment(id, submissionId),
          setIsCancelling,
          APPT_ACTIONS_CANCEL_FALLBACK,
        );
      }
      return runAction(
        () => ApiService.cancelAppointment(submissionId),
        setIsCancelling,
        APPT_ACTIONS_CANCEL_FALLBACK,
      );
    },
    [scope, teamId, runAction],
  );

  const rescheduleAppointment = useCallback(
    async (submissionId: string, newAppointmentTimeUtc: string): Promise<boolean> => {
      if (scope === 'team') {
        if (!teamId) {
          setError(APPT_ACTIONS_TEAM_ID_REQUIRED);
          return false;
        }
        const id = teamId;
        return runAction(
          () => ApiService.teamRescheduleAppointment(id, submissionId, { newAppointmentTimeUtc }),
          setIsRescheduling,
          APPT_ACTIONS_RESCHEDULE_FALLBACK,
        );
      }
      return runAction(
        () => ApiService.rescheduleAppointment(submissionId, { newAppointmentTimeUtc }),
        setIsRescheduling,
        APPT_ACTIONS_RESCHEDULE_FALLBACK,
      );
    },
    [scope, teamId, runAction],
  );

  return { cancelAppointment, rescheduleAppointment, isCancelling, isRescheduling, error };
}
